import React from "react";
import { X } from "lucide-react";
import type { InvoiceItem, TaxDocumentBuyer } from "../../../../domain/types/tax-document";
import { resolveErrorMessage } from "../../../../shared/utils/save-flow";
import { TaxDocumentBuyerForm, toBuyerPayload } from "./TaxDocumentBuyerForm";
import { taxDocumentApi } from "./tax-document-api";

interface Props {
  companyId: number;
  onClose: () => void;
  onCreated: (invoice: InvoiceItem) => void;
}

/** 전화·메일로 받은 증빙 요청을 주문번호로 등록한다. 발급은 등록 후 상세에서 한다 */
export const InvoiceCreateDialog: React.FC<Props> = ({ companyId, onClose, onCreated }) => {
  const [orderNumber, setOrderNumber] = React.useState("");
  const [buyer, setBuyer] = React.useState<TaxDocumentBuyer>({ invoiceType: "CASH_RECEIPT", cashReceiptUsage: "INCOME_DEDUCTION" });
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleSubmit = async () => {
    if (!orderNumber.trim()) {
      setError("주문번호를 입력해 주세요.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const created = await taxDocumentApi.create(companyId, orderNumber.trim(), toBuyerPayload(buyer));
      onCreated(created);
    } catch (e) {
      setError(resolveErrorMessage(e, "등록에 실패했습니다."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[5000] flex items-center justify-center bg-black/40">
      <div className="w-[560px] max-h-[90vh] rounded-[12px] bg-white shadow-lg overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-[#eee]">
          <h3 className="text-[16px] font-bold text-[#222]">세금증빙 등록</h3>
          <button onClick={onClose} className="text-[#999] hover:text-[#666]" aria-label="닫기">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 overflow-y-auto flex flex-col gap-4">
          {error && (
            <div className="rounded-[8px] border border-[#F24A4A]/20 bg-[#FEF2F2] px-3 py-2 text-[13px] text-[#F24A4A]">{error}</div>
          )}
          <label className="flex flex-col gap-1.5">
            <span className="text-[12px] font-medium text-[#555]">
              주문번호<span className="text-[#4285F4] ml-0.5">*</span>
            </span>
            <input
              value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value)}
              className="w-full h-[40px] px-3 text-[13px] border border-[#e0e0e0] rounded-[8px] focus:border-[#4285F4] focus:outline-none"
              placeholder="주문 관리의 주문번호"
            />
          </label>
          <TaxDocumentBuyerForm value={buyer} onChange={setBuyer} disabled={busy} />
        </div>
        <div className="p-4 border-t border-[#eee] flex justify-end gap-2">
          <button onClick={onClose} className="px-4 h-[36px] text-[13px] rounded-[6px] border border-[#222] hover:bg-[#f5f5f5]">
            닫기
          </button>
          <button
            disabled={busy}
            onClick={handleSubmit}
            className="px-4 h-[36px] text-[13px] rounded-[6px] bg-[#222] text-white hover:bg-[#333] disabled:bg-[#ccc]"
          >
            {busy ? "등록 중..." : "등록"}
          </button>
        </div>
      </div>
    </div>
  );
};
